import { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { StoreModal } from "../components/StoreModal";
import {
  useGetBookingsQuery,
  useUpdateProfileMutation,
  useUploadAvatarMutation,
  useChangePasswordMutation,
  useGetCustomBookingsQuery,
  useUserRespondCustomBookingMutation,
} from "../store/api/servicesApi";
import fallbackImage from "../assets/images/service.jpg";

const STATUS_STYLES = {
  pending:   "bg-blue-100 text-blue-600",
  accepted:  "bg-green-100 text-green-600",
  declined:  "bg-red-100 text-red-500",
  completed: "bg-gray-100 text-gray-600",
  cancelled: "bg-orange-100 text-orange-500",
};

const STATUS_LABELS = {
  pending:   "Pending",
  accepted:  "Accepted",
  declined:  "Declined",
  completed: "Completed",
  cancelled: "Cancelled",
};

function AccountTab({ user }) {
  const { updateUser } = useAuth();
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();
  const [uploadAvatar, { isLoading: isUploading }] = useUploadAvatarMutation();

  const [form, setForm] = useState({
    name:    user?.name    || "",
    phone:   user?.phone   || "",
    address: user?.address || "",
  });
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [saveError, setSaveError]   = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fileRef = useRef();

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaveError("");
    setSaveSuccess(false);
    try {
      let avatarUrl = user?.avatar || null;

      if (selectedFile) {
        const fd = new FormData();
        fd.append("avatar", selectedFile);
        const uploadRes = await uploadAvatar(fd).unwrap();
        avatarUrl = uploadRes.data?.url || uploadRes.url || avatarUrl;
      }

      const updated = await updateProfile({
        name:    form.name,
        phone:   form.phone,
        address: form.address,
        avatar:  avatarUrl,
      }).unwrap();

      updateUser(updated.data || updated);
      setSelectedFile(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.data?.message || "Failed to save changes. Please try again.");
    }
  };

  const busy = isSaving || isUploading;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="font-manrope-bold text-xl text-black">Account management</h2>

      {/* Avatar upload */}
      <div className="flex items-center gap-5">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center overflow-hidden flex-shrink-0">
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="font-manrope-bold text-2xl text-white uppercase">
                {(form.name || user?.email || "U")[0]}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => fileRef.current.click()}
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary border-2 border-white flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="white" className="w-3.5 h-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
            </svg>
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
        </div>
        <div>
          <p className="font-manrope-semibold text-sm text-black">{form.name || "Your name"}</p>
          <p className="font-manrope-regular text-xs text-secondary">{user?.email}</p>
        </div>
      </div>

      {/* Form grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="font-manrope-medium text-sm text-secondary">Full name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Enter your full name"
            className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="font-manrope-medium text-sm text-secondary flex items-center gap-1.5">
            Email address
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </label>
          <input
            type="email"
            value={user?.email || ""}
            readOnly
            className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-secondary bg-gray-50 cursor-not-allowed outline-none"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="font-manrope-medium text-sm text-secondary">WhatsApp number</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+1 000 000 0000"
            className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="font-manrope-medium text-sm text-secondary">Address</label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Enter your address"
            className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {saveError && (
        <p className="font-manrope-regular text-sm text-red-500">{saveError}</p>
      )}
      {saveSuccess && (
        <p className="font-manrope-regular text-sm text-green-600">Profile updated successfully.</p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={busy}
          className="font-manrope-semibold text-base text-white bg-primary hover:opacity-90 disabled:opacity-60 transition-opacity px-6 py-2.5 rounded-[12px] flex items-center gap-2"
        >
          {busy && (
            <svg className="animate-spin w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          )}
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function PasswordTab() {
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [form, setForm] = useState({ current: "", newPass: "", confirm: "" });
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError("");
    setSuccess(false);

    if (!form.current || !form.newPass || !form.confirm) {
      setError("All fields are required.");
      return;
    }
    if (form.newPass !== form.confirm) {
      setError("New passwords do not match.");
      return;
    }
    if (form.newPass.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    try {
      await changePassword({ currentPassword: form.current, newPassword: form.newPass }).unwrap();
      setSuccess(true);
      setForm({ current: "", newPass: "", confirm: "" });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.data?.message || "Failed to update password. Please try again.");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <h2 className="font-manrope-bold text-xl text-black">Change password</h2>

      <div className="flex flex-col gap-4 w-full">
        {[
          { label: "Current password", key: "current", placeholder: "Enter current password" },
          { label: "New password",     key: "newPass", placeholder: "Enter new password" },
          { label: "Confirm new password", key: "confirm", placeholder: "Confirm new password" },
        ].map(({ label, key, placeholder }) => (
          <div key={key} className="flex flex-col gap-1.5">
            <label className="font-manrope-medium text-sm text-secondary">{label}</label>
            <input
              type="password"
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              placeholder={placeholder}
              className="border border-border rounded-xl px-4 py-2.5 font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
            />
          </div>
        ))}

        {error && (
          <p className="font-manrope-regular text-sm text-red-500">{error}</p>
        )}
        {success && (
          <p className="font-manrope-regular text-sm text-green-600">Password updated successfully.</p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="font-manrope-semibold text-base text-white bg-primary hover:opacity-90 disabled:opacity-60 transition-opacity px-6 py-2.5 rounded-[12px] w-fit flex items-center gap-2"
        >
          {isLoading && (
            <svg className="animate-spin w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          )}
          {isLoading ? "Updating…" : "Update password"}
        </button>
      </div>
    </div>
  );
}

function CredentialsSection({ booking }) {
  const [copied, setCopied] = useState(null);
  const [storeModal, setStoreModal] = useState(null); // "ios" | "android" | null

  const { appUsername, appPassword, credentialsExpiresAt } = booking;
  const expiryMs = credentialsExpiresAt ? new Date(credentialsExpiresAt).getTime() : null;
  const now = Date.now();
  const isExpired = expiryMs ? now > expiryMs : false;

  const formatExpiry = () => {
    if (!expiryMs) return null;
    const diff = expiryMs - now;
    if (diff <= 0) return "Expired";
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `Expires in ${h}h ${m}m` : `Expires in ${m}m`;
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  if (!appUsername || !appPassword) return null;

  return (
    <>
      <div className="border-t border-gray-100" />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p className="font-manrope-bold text-base text-black">App credentials</p>
          {expiryMs && (
            <span className={`font-manrope-medium text-xs px-3 py-1 rounded-full ${isExpired ? "bg-red-100 text-red-500" : "bg-green-100 text-green-600"}`}>
              {formatExpiry()}
            </span>
          )}
        </div>

        {isExpired ? (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
            <p className="font-manrope-medium text-sm text-red-500 text-center">
              These credentials have expired.
            </p>
          </div>
        ) : (
          <>
            {/* Instruction */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 flex gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary flex-shrink-0 mt-0.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
              <p className="font-manrope-regular text-sm text-secondary leading-relaxed">
                Install the app from the App Store or Google Play and use these credentials to log in. They are valid until <span className="font-manrope-semibold text-black">30 minutes after your booking ends</span>.
              </p>
            </div>

            {/* Username */}
            <div className="flex flex-col gap-1.5">
              <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Username</span>
              <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                <span className="font-manrope-semibold text-sm text-black">{appUsername}</span>
                <button onClick={() => copyToClipboard(appUsername, "username")} className="flex items-center gap-1.5 text-primary hover:opacity-70 transition-opacity">
                  {copied === "username" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                    </svg>
                  )}
                  <span className="font-manrope-medium text-xs">{copied === "username" ? "Copied!" : "Copy"}</span>
                </button>
              </div>
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Password</span>
              <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                <span className="font-manrope-semibold text-sm text-black tracking-widest">{appPassword}</span>
                <button onClick={() => copyToClipboard(appPassword, "password")} className="flex items-center gap-1.5 text-primary hover:opacity-70 transition-opacity">
                  {copied === "password" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                    </svg>
                  )}
                  <span className="font-manrope-medium text-xs">{copied === "password" ? "Copied!" : "Copy"}</span>
                </button>
              </div>
            </div>

            {/* Store buttons */}
            <div className="flex flex-col gap-2">
              <p className="font-manrope-medium text-xs text-secondary">Download the app</p>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* App Store */}
                <button
                  onClick={() => setStoreModal("ios")}
                  className="flex-1 flex items-center gap-2.5 bg-black text-white px-4 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white flex-shrink-0">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left leading-tight">
                    <p className="font-manrope-regular text-[10px] text-white/70">Download on the</p>
                    <p className="font-manrope-bold text-sm">App Store</p>
                  </div>
                </button>

                {/* Google Play */}
                <button
                  onClick={() => setStoreModal("android")}
                  className="flex-1 flex items-center gap-2.5 bg-black text-white px-4 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0">
                    <path fill="#4CAF50" d="M1.22 0L13.4 12 1.22 24H.8V0h.42z"/>
                    <path fill="#FFD600" d="M16.9 8.6L4.03 1.1 13.4 12l3.5-3.4z"/>
                    <path fill="#F44336" d="M4.03 22.9L16.9 15.4 13.4 12 4.03 22.9z"/>
                    <path fill="#2196F3" d="M21.6 10.8L17.6 8.6 13.4 12l4.2 3.4 4-2.2c.8-.5.8-1.9 0-2.4z"/>
                  </svg>
                  <div className="text-left leading-tight">
                    <p className="font-manrope-regular text-[10px] text-white/70">Get it on</p>
                    <p className="font-manrope-bold text-sm">Google Play</p>
                  </div>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {storeModal && <StoreModal store={storeModal} onClose={() => setStoreModal(null)} />}
    </>
  );
}

function BookingDetail({ booking, onBack }) {
  const serviceImage = booking.service?.images?.[0] || fallbackImage;
  const statusLabel = STATUS_LABELS[booking.status] || booking.status;
  const canCancel = booking.status === "pending";

  const formattedDate = booking.date
    ? new Date(booking.date + "T00:00:00").toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      })
    : "—";

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-secondary">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h2 className="font-manrope-bold text-xl text-black">Booking details</h2>
      </div>

      {/* Service */}
      <div className="flex flex-col gap-3">
        <p className="font-manrope-bold text-base text-black">Service</p>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
            <img src={serviceImage} alt={booking.service?.title} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-manrope-semibold text-sm text-black">{booking.service?.title || "—"}</p>
            <p className="font-manrope-bold text-base text-primary">${booking.subtotal?.toFixed(2)}</p>
          </div>
          <span className={`font-manrope-semibold text-xs px-4 py-1.5 rounded-full flex-shrink-0 ${STATUS_STYLES[booking.status] || "bg-gray-100 text-gray-600"}`}>
            • {statusLabel}
          </span>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Info grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Booking #</span>
          <span className="font-manrope-semibold text-sm text-black">{booking.bookingNumber}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Guests</span>
          <span className="font-manrope-semibold text-sm text-black">{booking.quantity} {booking.quantity === 1 ? "person" : "people"}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Date</span>
          <span className="font-manrope-semibold text-sm text-black">{formattedDate}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Time slot</span>
          <span className="font-manrope-semibold text-sm text-black">
            {booking.timeSlot && booking.slotEndTime
              ? `${booking.timeSlot} – ${booking.slotEndTime}`
              : booking.timeSlot || "—"}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Number of people</span>
          <span className="font-manrope-semibold text-sm text-black">{booking.quantity} {booking.quantity === 1 ? "person" : "people"}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Total paid</span>
          <span className="font-manrope-bold text-sm text-primary">${booking.subtotal?.toFixed(2)}</span>
        </div>
        {booking.customerName && (
          <div className="flex flex-col gap-1">
            <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Name</span>
            <span className="font-manrope-semibold text-sm text-black">{booking.customerName}</span>
          </div>
        )}
        {booking.address && (
          <div className="flex flex-col gap-1">
            <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Address</span>
            <span className="font-manrope-semibold text-sm text-black">{booking.address}</span>
          </div>
        )}
        {booking.adminDecisionNote && (
          <div className="sm:col-span-2 flex flex-col gap-1">
            <span className="font-manrope-medium text-xs text-secondary uppercase tracking-wide">Admin note</span>
            <span className="font-manrope-regular text-sm text-black">{booking.adminDecisionNote}</span>
          </div>
        )}
      </div>

      {/* App credentials */}
      {booking.appUsername && <CredentialsSection booking={booking} />}

      <div className="border-t border-gray-100" />

      {/* Cancel */}
      {canCancel && (
        <div className="flex flex-col gap-2">
          <button className="w-full flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 transition-colors text-white font-manrope-semibold text-base py-3 rounded-xl">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel order
          </button>
          <p className="font-manrope-regular text-xs text-secondary text-center">
            Before canceling any order please read our{" "}
            <span className="text-primary font-manrope-medium cursor-pointer hover:underline">Cancellation policy</span>
          </p>
        </div>
      )}
    </div>
  );
}

function BookingsTab() {
  const [selectedBooking, setSelectedBooking] = useState(null);
  const { data, isLoading, isError } = useGetBookingsQuery();
  const bookings = data?.data || [];

  if (selectedBooking) {
    return <BookingDetail booking={selectedBooking} onBack={() => setSelectedBooking(null)} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="font-manrope-bold text-xl text-black">My bookings</h2>

      {isLoading && (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border border-gray-100 p-4 flex gap-4 animate-pulse">
              <div className="w-20 h-20 rounded-xl bg-gray-200 flex-shrink-0" />
              <div className="flex-1 flex flex-col gap-2 py-1">
                <div className="h-4 bg-gray-200 rounded w-3/5" />
                <div className="h-3 bg-gray-100 rounded w-2/5" />
                <div className="h-3 bg-gray-100 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div className="text-center py-10">
          <p className="font-manrope-medium text-sm text-red-500">Failed to load bookings. Please try again.</p>
        </div>
      )}

      {!isLoading && !isError && bookings.length === 0 && (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
          </div>
          <div>
            <p className="font-manrope-semibold text-base text-black">No bookings yet</p>
            <p className="font-manrope-regular text-sm text-secondary mt-1">Your bookings will appear here once you make one.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && bookings.length > 0 && (
        <div className="flex flex-col gap-4">
          {bookings.map((booking) => {
            const serviceImage = booking.service?.images?.[0] || fallbackImage;
            const statusLabel = STATUS_LABELS[booking.status] || booking.status;
            const formattedDate = booking.date
              ? new Date(booking.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : "—";
            return (
              <div
                key={booking.id}
                className="border border-gray-100 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
                onClick={() => setSelectedBooking(booking)}
              >
                <div className="w-full h-40 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                  <img src={serviceImage} alt={booking.service?.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 flex flex-col gap-1 min-w-0">
                  <p className="font-manrope-semibold text-base text-black truncate">{booking.service?.title || "Service"}</p>
                  <p className="font-manrope-bold text-base text-primary">${booking.subtotal?.toFixed(2)}</p>
                  <p className="font-manrope-regular text-xs text-secondary">#{booking.bookingNumber}</p>
                  <p className="font-manrope-regular text-xs text-secondary">
                    {formattedDate}{booking.timeSlot ? ` · ${booking.timeSlot}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-manrope-semibold text-xs px-3 py-1 rounded-full ${STATUS_STYLES[booking.status] || "bg-gray-100 text-gray-600"}`}>
                    {statusLabel}
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const CB_STATUS_STYLES = {
  pending:        "bg-blue-100 text-blue-600",
  price_offered:  "bg-purple-100 text-purple-600",
  user_countered: "bg-orange-100 text-orange-500",
  agreed:         "bg-teal-100 text-teal-600",
  declined:       "bg-red-100 text-red-500",
  cancelled:      "bg-gray-100 text-gray-500",
};
const CB_STATUS_LABELS = {
  pending:        "Pending review",
  price_offered:  "Price offered",
  user_countered: "Counter sent",
  agreed:         "Agreed",
  declined:       "Declined",
  cancelled:      "Cancelled",
};

function NegotiationTimeline({ negotiations = [] }) {
  if (!negotiations.length) return null;
  return (
    <div className="flex flex-col gap-2 mt-3">
      {negotiations.map((n, i) => {
        const isAdmin = n.by === "admin";
        return (
          <div key={i} className={`rounded-xl px-4 py-3 ${isAdmin ? "bg-primary/5 border border-primary/10" : "bg-gray-50 border border-gray-100"}`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`font-manrope-semibold text-xs uppercase tracking-wide ${isAdmin ? "text-primary" : "text-secondary"}`}>
                {isAdmin ? "Admin" : "You"} · {n.action}
              </span>
              {n.price != null && (
                <span className="font-manrope-bold text-sm text-black">${Number(n.price).toFixed(2)}</span>
              )}
            </div>
            {n.message && <p className="font-manrope-regular text-sm text-secondary leading-relaxed">{n.message}</p>}
            <p className="font-manrope-regular text-xs text-gray-400 mt-1">
              {new Date(n.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function CounterOfferModal({ currentPrice, onClose, onConfirm, isLoading }) {
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");
  const [err, setErr] = useState("");

  const handle = () => {
    if (!price || isNaN(Number(price)) || Number(price) <= 0) { setErr("Enter a valid price."); return; }
    onConfirm({ action: "counter", price: Number(price), message });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-manrope-bold text-base text-black">Send counter offer</h3>
          <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-gray-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {currentPrice != null && (
          <p className="font-manrope-regular text-sm text-secondary mb-3">
            Admin offered <span className="font-manrope-bold text-primary">${Number(currentPrice).toFixed(2)}</span>. Enter your counter price:
          </p>
        )}
        <div className="mb-3">
          <label className="font-manrope-medium text-sm text-secondary block mb-1.5">Your price (USD)</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-manrope-semibold text-sm text-gray-400">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => { setPrice(e.target.value); setErr(""); }}
              placeholder="0.00"
              className="w-full pl-7 pr-4 py-2.5 border border-border rounded-xl font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="font-manrope-medium text-sm text-secondary block mb-1.5">Message (optional)</label>
          <textarea
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Explain your offer…"
            className="w-full px-4 py-3 border border-border rounded-xl font-manrope-regular text-sm text-black outline-none focus:border-primary transition-colors resize-none"
          />
        </div>
        {err && <p className="font-manrope-regular text-sm text-red-500 mb-3">{err}</p>}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl font-manrope-semibold text-sm text-secondary hover:bg-gray-50 transition-colors">Cancel</button>
          <button onClick={handle} disabled={isLoading} className="flex-1 py-2.5 bg-primary text-white rounded-xl font-manrope-semibold text-sm hover:opacity-90 disabled:opacity-60 transition-opacity flex items-center justify-center gap-2">
            {isLoading && <svg className="animate-spin w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>}
            {isLoading ? "Sending…" : "Send offer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomRequestDetail({ booking, onBack }) {
  const [showCounter, setShowCounter] = useState(false);
  const [actionError, setActionError] = useState("");
  const [userRespond, { isLoading: isResponding }] = useUserRespondCustomBookingMutation();

  const dateStr = booking.preferredDate
    ? new Date(booking.preferredDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    : "—";
  const slotStr = booking.preferredTimeSlot || "—";

  const canRespond = booking.status === "price_offered";

  const handleAccept = async () => {
    setActionError("");
    try {
      await userRespond({ id: booking.id, action: "accept" }).unwrap();
    } catch (err) {
      setActionError(err?.data?.error?.message || "Failed to accept offer.");
    }
  };

  const handleCounter = async (body) => {
    setActionError("");
    try {
      await userRespond({ id: booking.id, ...body }).unwrap();
      setShowCounter(false);
    } catch (err) {
      setActionError(err?.data?.error?.message || "Failed to send counter offer.");
      throw err;
    }
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-secondary">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <h2 className="font-manrope-bold text-xl text-black">Request details</h2>
        </div>

        {/* Status + number */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`font-manrope-semibold text-xs px-3 py-1 rounded-full ${CB_STATUS_STYLES[booking.status] || "bg-gray-100 text-gray-600"}`}>
            • {CB_STATUS_LABELS[booking.status] || booking.status}
          </span>
          <span className="font-manrope-regular text-xs text-secondary">#{booking.bookingNumber}</span>
        </div>

        {/* Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="font-manrope-medium text-xs text-secondary uppercase tracking-wide mb-0.5">Date</p>
            <p className="font-manrope-semibold text-sm text-black">{dateStr}</p>
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="font-manrope-medium text-xs text-secondary uppercase tracking-wide mb-0.5">Time slot</p>
            <p className="font-manrope-semibold text-sm text-black">{slotStr}</p>
          </div>
          <div className="sm:col-span-2 bg-gray-50 rounded-xl px-4 py-3">
            <p className="font-manrope-medium text-xs text-secondary uppercase tracking-wide mb-0.5">Description</p>
            <p className="font-manrope-regular text-sm text-black leading-relaxed">{booking.serviceDescription}</p>
          </div>
          {booking.currentPrice != null && (
            <div className="sm:col-span-2 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
              <p className="font-manrope-medium text-xs text-primary uppercase tracking-wide mb-0.5">
                {booking.status === "agreed" ? "Agreed price" : "Current offer"}
              </p>
              <p className="font-manrope-bold text-xl text-primary">${Number(booking.currentPrice).toFixed(2)}</p>
            </div>
          )}
        </div>

        {/* Negotiation history */}
        {booking.negotiations?.length > 0 && (
          <div>
            <p className="font-manrope-bold text-base text-black mb-2">Negotiation history</p>
            <NegotiationTimeline negotiations={booking.negotiations} />
          </div>
        )}

        {/* App credentials for confirmed custom requests */}
        <CredentialsSection booking={booking} />

        {/* Actions for customer */}
        {canRespond && (
          <div className="flex flex-col gap-3">
            <p className="font-manrope-semibold text-sm text-black">Respond to offer</p>
            {actionError && <p className="font-manrope-regular text-sm text-red-500">{actionError}</p>}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAccept}
                disabled={isResponding}
                className="flex-1 py-3 bg-green-500 text-white font-manrope-semibold text-sm rounded-xl hover:bg-green-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {isResponding && <svg className="animate-spin w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>}
                Accept offer ${Number(booking.currentPrice || 0).toFixed(2)}
              </button>
              <button
                onClick={() => setShowCounter(true)}
                disabled={isResponding}
                className="flex-1 py-3 border border-primary text-primary font-manrope-semibold text-sm rounded-xl hover:bg-primary/5 disabled:opacity-50 transition-colors"
              >
                Make counter offer
              </button>
            </div>
          </div>
        )}

        {booking.status === "user_countered" && (
          <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 text-center">
            <p className="font-manrope-semibold text-sm text-orange-600">Counter offer sent</p>
            <p className="font-manrope-regular text-xs text-orange-400 mt-0.5">Waiting for admin to respond to your counter offer.</p>
          </div>
        )}

        {booking.status === "agreed" && (
          <div className="bg-teal-50 border border-teal-100 rounded-xl px-4 py-3 text-center">
            <p className="font-manrope-semibold text-sm text-teal-600">Price agreed — booking confirmed!</p>
            <p className="font-manrope-regular text-xs text-teal-400 mt-0.5">Our team will be in touch with further details.</p>
          </div>
        )}

        {booking.status === "declined" && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-center">
            <p className="font-manrope-semibold text-sm text-red-500">Request declined</p>
            <p className="font-manrope-regular text-xs text-red-400 mt-0.5">Unfortunately we couldn't accommodate this request.</p>
          </div>
        )}
      </div>

      {showCounter && (
        <CounterOfferModal
          currentPrice={booking.currentPrice}
          onClose={() => setShowCounter(false)}
          onConfirm={handleCounter}
          isLoading={isResponding}
        />
      )}
    </>
  );
}

function CustomRequestsTab() {
  const [selected, setSelected] = useState(null);
  const { data, isLoading, isError } = useGetCustomBookingsQuery();
  const bookings = data?.data || [];

  if (selected) {
    const fresh = bookings.find((b) => b.id === selected.id) || selected;
    return <CustomRequestDetail booking={fresh} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="font-manrope-bold text-xl text-black">Custom requests</h2>

      {isLoading && (
        <div className="flex flex-col gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="rounded-2xl border border-gray-100 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-2/5 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-3/5" />
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div className="text-center py-10">
          <p className="font-manrope-medium text-sm text-red-500">Failed to load requests. Please try again.</p>
        </div>
      )}

      {!isLoading && !isError && bookings.length === 0 && (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
            </svg>
          </div>
          <div>
            <p className="font-manrope-semibold text-base text-black">No custom requests yet</p>
            <p className="font-manrope-regular text-sm text-secondary mt-1">Submit a custom booking request to get started.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && bookings.length > 0 && (
        <div className="flex flex-col gap-3">
          {bookings.map((b) => {
            const dateStr = b.preferredDate
              ? new Date(b.preferredDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : "—";
            const slotStr = b.preferredTimeSlot || "—";
            const needsAction = b.status === "price_offered";
            return (
              <div
                key={b.id}
                onClick={() => setSelected(b)}
                className="border border-gray-100 rounded-2xl p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`font-manrope-semibold text-xs px-3 py-1 rounded-full ${CB_STATUS_STYLES[b.status] || "bg-gray-100 text-gray-600"}`}>
                      • {CB_STATUS_LABELS[b.status] || b.status}
                    </span>
                    {needsAction && (
                      <span className="font-manrope-bold text-xs bg-primary text-white px-2 py-0.5 rounded-full animate-pulse">Action needed</span>
                    )}
                  </div>
                  <p className="font-manrope-semibold text-sm text-black truncate">{b.serviceDescription}</p>
                  <p className="font-manrope-regular text-xs text-secondary mt-0.5">
                    {dateStr} · {slotStr} · #{b.bookingNumber}
                  </p>
                  {b.currentPrice != null && (
                    <p className="font-manrope-bold text-sm text-primary mt-1">${Number(b.currentPrice).toFixed(2)}</p>
                  )}
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-gray-400 flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: "account", label: "Account management", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  )},
  { key: "password", label: "Change password", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  )},
  { key: "bookings", label: "Manage bookings", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  )},
  { key: "custom", label: "Custom requests", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  )},
];

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("account");

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="bg-bg min-h-screen py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-6 items-start">

          {/* Sidebar — horizontal scrollable tabs on mobile, vertical rail on desktop */}
          <div className="w-full lg:w-64 lg:flex-shrink-0 bg-white rounded-2xl p-3 lg:p-5 shadow-sm flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl font-manrope-medium text-sm transition-colors text-left flex-shrink-0 lg:w-full whitespace-nowrap
                  ${activeTab === tab.key
                    ? "bg-primary text-white"
                    : "text-secondary hover:bg-gray-50 hover:text-black"
                  }`}
              >
                <span className={activeTab === tab.key ? "text-white" : "text-secondary"}>
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            ))}

            <div className="lg:mt-2 lg:pt-2 lg:border-t lg:border-gray-100 flex-shrink-0">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-xl font-manrope-medium text-sm text-red-500 border border-red-200 hover:bg-red-50 transition-colors w-full whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                </svg>
                Log out
              </button>
            </div>
          </div>

          {/* Content panel */}
          <div className="flex-1 min-w-0 bg-white rounded-2xl p-4 sm:p-6 shadow-sm w-full">
            {activeTab === "account" && <AccountTab user={user} />}
            {activeTab === "password" && <PasswordTab />}
            {activeTab === "bookings" && <BookingsTab />}
            {activeTab === "custom" && <CustomRequestsTab />}
          </div>

        </div>
      </div>
    </div>
  );
}
